<template>
  <div class="page page-project">
    <div class="page__wrap" data-animate="page-wrap" v-if="finishing">
      <NavigationBackLink
        class="row-1 p-top-5"
        data-animate="header-content"
        :link="{
          title: $t('bucket.backLinkFinishes'),
          route: {
            name: 'finishes',
          },
        }"
      />

      <PagesFinishingHeader
        :finishing="finishing"
        class="row-1 m-top-5"
        :class="{
          'p-bot-3': !formattedProducts.length,
        }"
      />
      <!-- Products -->
      <template v-if="formattedProducts.length">
        <div class="row-1 m-top-3" data-animate="page-content">
          <h2 class="title m-right-16px">
            {{
              $t('pages.product.finishingsProductsTitle', {
                finishing: finishing.finishingColorLabel || finishing.title,
              })
            }}
          </h2>
        </div>

        <LazyListingList
          class="row-1 m-top-5"
          :class="{
            'p-bot-3': count <= limit,
          }"
          :items="formattedProducts"
        />
        <LazyWidgetPaginationListing
          v-if="count > limit"
          class="row-1 flex --justify-center m-top-5 p-bot-3"
          :itemPerPage="limit"
          :total="count"
        />
      </template>
    </div>
  </div>
</template>

<script setup>
import { useCraftStore } from '@/store/craft.js'

import queries from '@/graphql/craft/queries/index.js'

import {
  onBeforeEnter,
  onEnter,
  onLeave,
} from '~/assets/js/page-transitions/listing.js'

definePageMeta({
  layout: 'default',
  pageTransition: {
    onBeforeEnter: (el) => {
      onBeforeEnter(el)
    },
    onEnter: async (el, done) => {
      const tl = onEnter(el)
      await tl.delay(0.5).play()
      done()
    },
    onLeave: async (el, done) => {
      const tl = onLeave(el)
      await tl.play()
      window.lenis.scrollTo(0, { immediate: true })
      done()
    },
  },
})

const { t } = useI18n()

// Fetch Craft
const { configuration } = useCraftStore()
const route = useRoute()

const limit = ref(15)

const query = queries.finishings.getFinishing
const variables = computed(() => ({
  site: configuration.site,
  marketId: configuration.marketId,
  slug: route.params.slug,
  limit: limit.value,
  offset: route.query?.page
    ? (parseInt(route.query?.page) - 1) * limit.value
    : 0,
}))
const { data } = await useGraphql(query, variables)

if (!data.value?.finishingsEntries?.[0]) {
  throw createError({ statusCode: 404 })
}

const finishing = computed(() => data.value?.finishingsEntries?.[0])
const formattedProducts = computed(() => {
  return data.value.productsEntries.map((product) => {
    return {
      title: product.title,
      cover: product.coverPortrait?.[0] || product.cover?.[0],
      route: {
        name: 'product-slug',
        params: { slug: product.slug },
      },
    }
  })
})

const count = computed(() => data.value?.entryCount || 0)
const seo = computed(() => data.value?.finishingsEntries?.[0]?.seo || {})
const localeParams = computed(
  () => data.value?.finishingsEntries?.[0]?.localized || {}
)

// Seo
useSeo({
  ...seo.value,
  localeParams: useHelpers().generateSeoLocaleParams(localeParams, 'slug'),
  meta: [
    {
      name: 'description',
      content:
        seo.value.meta?.description ||
        t('bucket.seoFinishesDescriptionPlaceholder', {
          finishing: finishing.value?.title,
        }),
    },
  ],
})
</script>
